const LOG_ENABLED = true;
const LOG_LEVELS_ENABLED = {
  log: true,
  info: true,
  warn: true,
  error: true,
  debug: true
};

class OfflineLogger {
  private static fallbackLogs: Array<{action: string, detail: string, timestamp: number}> = [];
  private static readonly MAX_FALLBACK_LOGS = 100;
  private static dbErrorsLogged = 0;
  private static readonly MAX_DB_ERRORS = 5;
  
  /**
   * Write a generic log entry to the offline logger.
   *
   * @param action - Identifier for the component or process.
   * @param detail - Message or object with additional information.
   */
  static log(action: string, detail: string | object) {
    const detailStr = typeof detail === 'object' ? JSON.stringify(detail) : detail;
    const timestamp = Date.now();
    
    if (LOG_ENABLED && LOG_LEVELS_ENABLED.log) console.log(`[OFFLINE] ${action}: ${detailStr}`);
    
    this.addToFallbackLogs(action, detailStr, timestamp);
    this.storeLogInDatabase(action, detailStr, timestamp).catch(() => {});
  }

  static info(action: string, detail: string | object) {
    const detailStr = typeof detail === 'object' ? JSON.stringify(detail) : detail;
    const timestamp = Date.now();

    if (LOG_ENABLED && LOG_LEVELS_ENABLED.info) console.info(`[OFFLINE INFO] ${action}: ${detailStr}`);
    
    this.addToFallbackLogs(`INFO ${action}`, detailStr, timestamp);
    this.storeLogInDatabase(`INFO ${action}`, detailStr, timestamp).catch(() => {});
  }

  static debug(action: string, detail: string | object) {
    const detailStr = typeof detail === 'object' ? JSON.stringify(detail) : detail;
    const timestamp = Date.now();

    if (LOG_ENABLED && LOG_LEVELS_ENABLED.debug) console.debug(`[OFFLINE DEBUG] ${action}: ${detailStr}`);

    this.addToFallbackLogs(`DEBUG ${action}`, detailStr, timestamp);
    this.storeLogInDatabase(`DEBUG ${action}`, detailStr, timestamp).catch(() => {});
  }

  static error(action: string, error: any) {
    const errorDetail = error instanceof Error 
      ? `${error.name}: ${error.message}` 
      : String(error);
    const timestamp = Date.now();
    
    console.error(`[OFFLINE ERROR] ${action}:`, error);
    
    this.addToFallbackLogs(`ERROR ${action}`, errorDetail, timestamp);
    this.storeLogInDatabase(`ERROR ${action}`, errorDetail, timestamp).catch(() => {});
  }

  // ✅ FIX: Updated signature for warn method
  static warn(action: string, detail: string | object, error?: any) {
    const detailStr = typeof detail === 'object' ? JSON.stringify(detail) : detail;
    const timestamp = Date.now();
    
    console.warn(`[OFFLINE WARNING] ${action}: ${detailStr}`);
    if (error) {
      console.warn('Error details:', error);
    }
    
    this.addToFallbackLogs(`WARNING ${action}`, detailStr, timestamp);
    this.storeLogInDatabase(`WARNING ${action}`, detailStr, timestamp).catch(() => {});
  }

  private static async storeLogInDatabase(action: string, detail: string, timestamp: number) {
    try {
      const { offlineDb } = await import('../db/offlineDb');
      
      // First, check if DB is open without causing side effects
      const isDbOpen = offlineDb.isOpen();
      
      if (!isDbOpen) {
        // Don't try to reopen too many times if there are consistent errors
        if (this.dbErrorsLogged >= this.MAX_DB_ERRORS) {
          return;
        }
        
        try {
          await offlineDb.open();
        } catch (err) {
          this.dbErrorsLogged++;
          console.warn('Failed to open database for logging:', err);
          return;
        }
      } else {
        // Reset the error counter if DB is working
        this.dbErrorsLogged = 0;
      }
      
      // Now try to add the log to the database
      await offlineDb.logs.add({
        timestamp,
        level: 'log',
        component: action,
        message: detail,
        action,
        detail
      });
    } catch (err) {
      // Just silently fail, we already have the log in fallback storage
      if (this.dbErrorsLogged < this.MAX_DB_ERRORS) {
        this.dbErrorsLogged++;
      }
    }
  }

  private static addToFallbackLogs(action: string, detail: string, timestamp: number) {
    this.fallbackLogs.push({ action, detail, timestamp });
    // Keep only the most recent logs if we exceed the limit
    if (this.fallbackLogs.length > this.MAX_FALLBACK_LOGS) {
      this.fallbackLogs = this.fallbackLogs.slice(-this.MAX_FALLBACK_LOGS);
    }
  }

  static async getLogs(limit: number = 100) {
    try {
      const { offlineDb } = await import('../db/offlineDb');
      
      // Check if DB is open and try to open it if needed
      if (!offlineDb.isOpen()) {
        try {
          await offlineDb.open();
        } catch (err) {
          console.warn('Failed to open database when getting logs:', err);
          return this.getFallbackLogs(limit);
        }
      }
      
      // Try to get logs from the database
      const logs = await offlineDb.logs
        .orderBy('timestamp')
        .reverse()
        .limit(limit)
        .toArray();
      
      // If no database logs, use fallback
      if (!logs || logs.length === 0) {
        return this.getFallbackLogs(limit);
      }
      
      // Convert to expected format
      return logs.map(log => ({
        action: log.action || log.component,
        detail: log.detail || log.message,
        timestamp: log.timestamp
      }));
    } catch (err) {
      console.warn('Failed to get logs from database:', err);
      return this.getFallbackLogs(limit);
    }
  }
  
  private static getFallbackLogs(limit: number) {
    return this.fallbackLogs
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, limit);
  }

  static async clearLogs() {
    // Always clear fallback logs
    this.fallbackLogs = [];
    
    try {
      const { offlineDb } = await import('../db/offlineDb');
      
      // Try to open the DB if it's closed
      if (!offlineDb.isOpen()) {
        try {
          await offlineDb.open();
        } catch (err) {
          console.error('Failed to open database for clearing logs:', err);
          return true; // We cleared the fallback logs at least
        }
      }
      
      // Clear the database logs
      await offlineDb.logs.clear();
      return true;
    } catch (err) {
      console.error('Failed to clear logs from database:', err);
      return false;
    }
  }
}

export default OfflineLogger;
